import { cloneElement, isValidElement, useId } from 'react'
import type { ReactElement, ReactNode } from 'react'
import './FormField.css'

export interface FormFieldProps {
  label: ReactNode
  /** Field control — an <input>, <textarea>, or <select>. Gets id + aria wiring. */
  children: ReactElement<{
    id?: string
    'aria-describedby'?: string
    'aria-invalid'?: boolean | 'true' | 'false'
    'aria-required'?: boolean
  }>
  /** Error message; when present, wires aria-invalid + aria-describedby. */
  error?: ReactNode
  /** Non-error helper text. */
  hint?: ReactNode
  required?: boolean
  className?: string
  /** Provide an explicit id if the control needs a stable one. */
  htmlFor?: string
}

/**
 * Accessible form field (KAN-1179): label + control + error/hint, correctly wired
 * with `htmlFor`, `aria-describedby`, `aria-invalid`, and `aria-required`. The child
 * control is cloned so callers don't repeat the id/aria plumbing.
 */
export function FormField({ label, children, error, hint, required, className = '', htmlFor }: FormFieldProps) {
  const generatedId = useId()
  const controlId = htmlFor ?? children.props.id ?? generatedId
  const errorId = `${controlId}-error`
  const hintId = `${controlId}-hint`

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined

  const control = isValidElement(children)
    ? cloneElement(children, {
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined
      })
    : children

  return (
    <div className={`ui-field ${error ? 'ui-field--error' : ''} ${className}`.trim()}>
      <label className="ui-field__label" htmlFor={controlId}>
        {label}
        {required && (
          <span className="ui-field__required" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      {control}
      {hint && !error && (
        <p id={hintId} className="ui-field__hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="ui-field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

export default FormField
